/**
 * fieldIds.ts — één plek waar de id's van een formulierveld vandaan komen.
 *
 * Waarom dit bestaat: het label wordt gerenderd door SFormElement.vue en de
 * input door een kindcomponent. Zolang beide hun eigen id afleidden, konden ze
 * uit elkaar lopen — en dat deden ze ook: het label had een hardgecodeerde
 * `for="colFormLabel"` die naar een element wees dat nergens bestond, waardoor
 * élk veld in het formulier een niet-gekoppeld label had.
 *
 * De oplossing is niet dat elk component deze functie zelf aanroept, maar dat
 * SFormElement de uitkomst één keer berekent en als prop doorgeeft. Dan kan er
 * per constructie geen verschil ontstaan.
 *
 * `element.name` is een veilige sleutel: SFormBuilder gebruikt hem al als
 * unieke sleutel in één platte formData over alle pagina's heen, dus namen
 * moeten sowieso uniek zijn.
 */

export interface FieldIds {
  /** Wrapper of groep (fieldset) */
  field: string;
  /** De control zelf: input, select of textarea */
  input: string;
  /** Het label of de legend */
  label: string;
  /** Uitleg onder het veld */
  help: string;
  /** Foutmelding */
  error: string;
  /** Id van optie nummer i binnen een radio- of checkboxgroep */
  option: (i: number) => string;
}

/**
 * Maak een id veilig voor gebruik in HTML en in CSS-selectors. Veldnamen komen
 * uit klantconfiguratie en kunnen spaties, punten of accenten bevatten; een
 * punt in een id breekt `querySelector('#...')`.
 */
function slug(naam: string): string {
  const schoon = String(naam || 'veld')
    .normalize('NFKD')
    .replace(/[^A-Za-z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
  // Altijd een letterprefix: een id mag niet met een cijfer beginnen.
  return `f-${schoon || 'veld'}`;
}

export function makeFieldIds(naam: string): FieldIds {
  const basis = slug(naam);
  return {
    field: basis,
    input: `${basis}-input`,
    label: `${basis}-label`,
    help: `${basis}-help`,
    error: `${basis}-error`,
    option: (i: number) => `${basis}-opt-${i}`,
  };
}

export default makeFieldIds;
